import { useEffect, useRef } from "react";

// -----------------------------------------------------------------
// DeepStarfieldCanvas — full-viewport ambient starfield sitting deep
// behind every dashboard surface. Paints ~180 twinkling stars, always-
// visible faint constellation lines between nearest neighbors, and a
// continuous cadence of shooting stars sweeping at ~-45°. Also brightens
// the constellation web for 3.5s whenever a `constellation-trigger`
// window event fires (health-score milestones, etc.). Respects
// prefers-reduced-motion. Silent — no audio.
// -----------------------------------------------------------------
export function DeepStarfieldCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;
    const reduced = typeof window !== "undefined"
      && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let W = 0, H = 0, raf = 0;
    let stars = [];
    let shootingStars = [];
    let lastShootingSpawn = 0;
    let constellationBoostUntil = 0;
    // Ambient rest-state lightning — hands-off alert cue that only fires
    // when the audit-risk tier is "high". Diagonal cross-viewport bolt
    // with jagged branching, cyan-to-rose gradient, fast 180ms decay.
    let riskTier = (typeof window !== "undefined" && window.__rsRiskTier) || "safe";
    const onRiskTier = (e) => {
      const t = e?.detail?.tier;
      if (t) riskTier = t;
    };
    let lightning = null;
    let lastLightningSpawn = 0;
    const nextLightningIn = () => 8000 + Math.random() * 6000; // 8-14s
    let lightningGap = nextLightningIn();

    // Shooting-star cadence: one every 6-10s for a high-frequency cosmic sweep.
    const nextShootingIn = () => 6_000 + Math.random() * 4_000;
    let shootingSpawnGap = nextShootingIn();

    const onBoost = () => { constellationBoostUntil = performance.now() + 3500; };
    if (typeof window !== "undefined") {
      window.addEventListener("constellation-trigger", onBoost);
      window.addEventListener("audit-risk-tier", onRiskTier);
    }

    const resize = () => {
      W = window.innerWidth;
      H = window.innerHeight;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      canvas.style.width = W + "px";
      canvas.style.height = H + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // Seed ~1 star per 6000 px² of viewport for a dense-but-not-noisy field.
      const target = Math.min(260, Math.max(120, Math.round((W * H) / 6000)));
      stars = new Array(target).fill(null).map(() => ({
        x: Math.random() * W,
        y: Math.random() * H,
        r: Math.random() * 1.3 + 0.35,
        tw: Math.random() * Math.PI * 2,
        ts: 0.4 + Math.random() * 1.4,
        // Tint band — mostly white, a pinch of pale-cyan and pale-gold
        tint: Math.random() < 0.15 ? [165, 243, 252] : Math.random() < 0.08 ? [253, 224, 71] : [255, 255, 255],
      }));
    };

    const spawnShootingStar = () => {
      shootingStars.push({
        x: Math.random() * W * 0.7,   // upper-left origin band
        y: -20,
        vx: 8 + Math.random() * 3,
        vy: 8 + Math.random() * 3,     // ≈ -45° trajectory (down-right)
        life: 0,
        maxLife: 70 + Math.random() * 40,
      });
    };

    const spawnLightning = () => {
      // Build a jagged diagonal bolt with 8-12 segments arcing across
      // ~55-90% of the viewport diagonal. Two side branches for drama.
      const startX = Math.random() * W * 0.4;
      const startY = -10;
      const segs = 10 + Math.floor(Math.random() * 4);
      const totalLen = Math.hypot(W, H) * (0.55 + Math.random() * 0.35);
      const baseAngle = Math.PI * 0.28 + (Math.random() - 0.5) * 0.35; // ~40-60° down-right
      const pts = [{ x: startX, y: startY }];
      let cx = startX, cy = startY, a = baseAngle;
      for (let s = 0; s < segs; s++) {
        a += (Math.random() - 0.5) * 0.55;
        const step = totalLen / segs;
        cx += Math.cos(a) * step;
        cy += Math.sin(a) * step;
        pts.push({ x: cx, y: cy });
      }
      // Two side branches — pick 2 random interior points and offshoot.
      const branches = [];
      for (let k = 0; k < 2; k++) {
        const idx = 2 + Math.floor(Math.random() * (pts.length - 3));
        const bx0 = pts[idx].x, by0 = pts[idx].y;
        const bpts = [{ x: bx0, y: by0 }];
        let ba = baseAngle + (Math.random() - 0.5) * 1.4;
        for (let s = 0; s < 4; s++) {
          ba += (Math.random() - 0.5) * 0.7;
          const step = 24 + Math.random() * 30;
          const nx = bpts[bpts.length - 1].x + Math.cos(ba) * step;
          const ny = bpts[bpts.length - 1].y + Math.sin(ba) * step;
          bpts.push({ x: nx, y: ny });
        }
        branches.push(bpts);
      }
      lightning = { pts, branches, born: performance.now(), life: 220, flashLife: 180 };
    };

    const paint = (t) => {
      // Fully clear each frame so nothing accumulates on the canvas — with
      // mix-blend-mode: screen, any dark fill above the nebula would still
      // read as a subtle veil. Shooting-star trails are short enough (~70
      // frames) that they read cleanly without a wash-based trail effect.
      ctx.clearRect(0, 0, W, H);

      // ---- Twinkling stars ----
      ctx.globalCompositeOperation = "lighter";
      for (const s of stars) {
        s.tw += 0.025 * s.ts;
        const alpha = 0.35 + 0.55 * (0.5 + 0.5 * Math.sin(s.tw));
        const [r, g, b] = s.tint;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${r},${g},${b},${alpha.toFixed(3)})`;
        ctx.fill();
        // Occasional gentle bloom for the brightest stars
        if (s.r > 1.2) {
          ctx.beginPath();
          ctx.arc(s.x, s.y, s.r * 2.4, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${r},${g},${b},${(alpha * 0.10).toFixed(3)})`;
          ctx.fill();
        }
      }

      // ---- Constellation web — always faint, brighter for 3.5s on trigger ----
      const boostRemaining = Math.max(0, (constellationBoostUntil - t) / 3500);
      const baseAlpha = 0.06;
      const boostAlpha = boostRemaining * 0.55;
      const totalAlpha = baseAlpha + boostAlpha;
      if (totalAlpha > 0.01) {
        const MAX_LINK_DIST = 130;
        ctx.strokeStyle = boostRemaining > 0
          ? `rgba(52, 211, 153, ${totalAlpha.toFixed(3)})`
          : `rgba(148, 197, 255, ${totalAlpha.toFixed(3)})`;
        ctx.lineWidth = 0.7;
        if (boostRemaining > 0) {
          ctx.shadowColor = "rgba(52,211,153,0.7)";
          ctx.shadowBlur = 5;
        }
        for (let i = 0; i < stars.length; i++) {
          const a = stars[i];
          // Sparse pairing so the web reads as ambient, not a heat map.
          for (let j = i + 1; j < stars.length; j += 4) {
            const b = stars[j];
            const dx = a.x - b.x;
            const dy = a.y - b.y;
            const d2 = dx * dx + dy * dy;
            if (d2 < MAX_LINK_DIST * MAX_LINK_DIST) {
              ctx.beginPath();
              ctx.moveTo(a.x, a.y);
              ctx.lineTo(b.x, b.y);
              ctx.stroke();
            }
          }
        }
        ctx.shadowBlur = 0;
      }

      // ---- Shooting stars ----
      if (t - lastShootingSpawn > shootingSpawnGap) {
        lastShootingSpawn = t;
        shootingSpawnGap = nextShootingIn();
        spawnShootingStar();
      }
      for (let i = shootingStars.length - 1; i >= 0; i--) {
        const sh = shootingStars[i];
        sh.x += sh.vx;
        sh.y += sh.vy;
        sh.life += 1;
        const fade = 1 - sh.life / sh.maxLife;
        if (fade <= 0 || sh.x > W + 60 || sh.y > H + 60) {
          shootingStars.splice(i, 1);
          continue;
        }
        // Tail — fading neon-cyan line trailing behind the head
        const tailLen = 120;
        const norm = Math.hypot(sh.vx, sh.vy);
        const tx = sh.x - (sh.vx / norm) * tailLen;
        const ty = sh.y - (sh.vy / norm) * tailLen;
        const tailGrad = ctx.createLinearGradient(tx, ty, sh.x, sh.y);
        tailGrad.addColorStop(0.0, "rgba(34,211,238,0)");
        tailGrad.addColorStop(0.7, `rgba(34,211,238,${(0.55 * fade).toFixed(3)})`);
        tailGrad.addColorStop(1.0, `rgba(224,255,255,${(0.95 * fade).toFixed(3)})`);
        ctx.strokeStyle = tailGrad;
        ctx.lineWidth = 2.2;
        ctx.beginPath();
        ctx.moveTo(tx, ty);
        ctx.lineTo(sh.x, sh.y);
        ctx.stroke();
        // Head
        ctx.beginPath();
        ctx.arc(sh.x, sh.y, 2.6, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(224,255,255,${fade.toFixed(3)})`;
        ctx.shadowColor = "rgba(34,211,238,0.9)";
        ctx.shadowBlur = 8;
        ctx.fill();
        ctx.shadowBlur = 0;
      }

      // ---- Ambient rest-state lightning — only when audit-risk is "high" ----
      if (riskTier === "high") {
        if (t - lastLightningSpawn > lightningGap) {
          lastLightningSpawn = t;
          lightningGap = nextLightningIn();
          spawnLightning();
        }
        if (lightning) {
          const age = t - lightning.born;
          if (age >= lightning.life) {
            lightning = null;
          } else {
            const fade = 1 - age / lightning.life;
            // Whole-sky flash — a translucent rose wash on the earliest
            // frames so the strike reads as a real atmospheric burst.
            if (age < lightning.flashLife) {
              const flashA = 0.08 * (1 - age / lightning.flashLife);
              ctx.fillStyle = `rgba(255, 228, 230, ${flashA.toFixed(3)})`;
              ctx.fillRect(0, 0, W, H);
            }
            ctx.lineCap = "round";
            ctx.lineJoin = "round";
            const drawPoly = (pts, width) => {
              const p0 = pts[0];
              const pN = pts[pts.length - 1];
              const grad = ctx.createLinearGradient(p0.x, p0.y, pN.x, pN.y);
              grad.addColorStop(0, `rgba(255, 240, 245, ${(0.95 * fade).toFixed(3)})`);
              grad.addColorStop(0.55, `rgba(244, 63, 94, ${(0.9 * fade).toFixed(3)})`);
              grad.addColorStop(1, `rgba(190, 18, 60, ${(0.5 * fade).toFixed(3)})`);
              ctx.strokeStyle = grad;
              ctx.lineWidth = width;
              ctx.shadowColor = "#f43f5e";
              ctx.shadowBlur = 14;
              ctx.beginPath();
              ctx.moveTo(p0.x, p0.y);
              for (let k = 1; k < pts.length; k++) ctx.lineTo(pts[k].x, pts[k].y);
              ctx.stroke();
            };
            drawPoly(lightning.pts, 1.8);
            for (const bp of lightning.branches) drawPoly(bp, 1.1);
            ctx.shadowBlur = 0;
          }
        }
      } else {
        lightning = null; // wipe any lingering bolt if tier de-escalates
      }

      if (!reduced) raf = requestAnimationFrame(paint);
    };

    resize();
    if (reduced) paint(performance.now());
    else raf = requestAnimationFrame(paint);
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      if (typeof window !== "undefined") {
        window.removeEventListener("constellation-trigger", onBoost);
        window.removeEventListener("audit-risk-tier", onRiskTier);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="deep-starfield-canvas"
      aria-hidden="true"
      className="fixed inset-0 pointer-events-none"
      style={{ zIndex: -5, mixBlendMode: "screen" }}
    />
  );
}
